import React from 'react';
import { Alert, Typography, Box } from '@mui/material';
import type { Board } from '../../types';

export interface BoardProgressGuidanceProps {
  board: Board;
  hasRecordedHits: boolean;
  claimedSquaresCount: number;
  allScoresRecorded: boolean;
}

export const BoardProgressGuidance: React.FC<BoardProgressGuidanceProps> = ({ board, hasRecordedHits, claimedSquaresCount, allScoresRecorded }) => {
  // Determine the current step and what's needed next
  const isDraft = board.status === 'draft';
  const isLive = board.status === 'live';
  const isLocked = board.status === 'locked';
  const hasRandomizedNumbers = board.numbersRevealed;

  // Calculate claimed cells (for non-standard modes, divide by squares per cell)
  const squaresPerCell = board.cellMode === 'quad' ? 4 : board.cellMode === 'paired' ? 2 : 1;
  const claimedCells = Math.floor(claimedSquaresCount / squaresPerCell);
  const totalCells = board.totalCells || 100;
  const unitName = 'squares'; // Always use "squares" terminology regardless of cell mode

  // Build the guidance message
  let severity: 'info' | 'warning' | 'success' = 'info';
  let title = '';
  let message = '';
  let steps: string[] = [];

  if (isDraft) {
    severity = 'info';
    title = 'Board Setup';
    message = 'Complete these steps to get your board ready:';
    steps = [
      claimedCells > 0 ? `✓ ${claimedCells}/${totalCells} ${unitName} claimed` : `Assign ${unitName} to players or share the invite code`,
      hasRandomizedNumbers ? '✓ Numbers randomized' : 'Randomize the numbers (can be done before or after publishing)',
      'Publish the board when ready to accept players',
    ];
  } else if (isLive && !hasRandomizedNumbers) {
    severity = 'warning';
    title = 'Numbers Not Randomized';
    message = 'Before recording scores, you must randomize the numbers. This ensures fair play.';
    steps = [
      'Click "Randomize Numbers" above',
      'The board will auto-lock when you record the first score',
    ];
  } else if (isLive && hasRandomizedNumbers && !hasRecordedHits) {
    severity = 'success';
    title = 'Ready for Game Day!';
    message = 'Your board is set up and ready to go.';
    steps = [
      `${claimedCells}/${totalCells} ${unitName} claimed`,
      'Numbers are randomized',
      `Lock the board now to prevent ${unitName.slice(0, -1)} changes, or it will auto-lock when you record the first score`,
    ];
  } else if (isLocked && !hasRecordedHits) {
    severity = 'info';
    title = 'Board Locked';
    message = `No more ${unitName.slice(0, -1)} claims allowed. Ready to record scores.`;
    steps = [];
  } else if ((isLive || isLocked) && hasRecordedHits && allScoresRecorded) {
    severity = 'success';
    title = 'All Scores Recorded';
    message = 'Complete the board to finalize results and mark all winners.';
    steps = [];
  } else if ((isLive || isLocked) && hasRecordedHits && !allScoresRecorded) {
    // Game in progress - no need for guidance
    return null;
  }

  // Don't show if no guidance needed
  if (!title) {
    return null;
  }

  return (
    <Alert severity={severity} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: steps.length > 0 ? 1 : 0 }}>
        {title}
      </Typography>
      {message && (
        <Typography variant="body2" sx={{ mb: steps.length > 0 ? 1 : 0 }}>
          {message}
        </Typography>
      )}
      {steps.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {steps.map((step, i) => (
            <li key={i}>
              <Typography variant="body2">{step}</Typography>
            </li>
          ))}
        </Box>
      )}
    </Alert>
  );
};

export default BoardProgressGuidance;
